import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import "@/landing-mystery.css";
import LandingMystery from "@/landing-mystery";

// Set to true to show the mystery landing page, false for the main site
const SHOW_MYSTERY_LANDING = true;

// Main site imports (only loaded when not showing mystery landing)
import axios from "axios";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import useEmblaCarousel from "embla-carousel-react";
import { 
  Facebook, 
  Instagram, 
  Phone, 
  Mail, 
  MapPin, 
  ChevronLeft, 
  ChevronRight,
  Menu,
  X,
  Heart,
  Baby,
  Cake,
  GraduationCap,
  PartyPopper,
  Calendar as CalendarIcon
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";

// Use relative /api path for Vercel serverless functions, or BACKEND_URL for other deployments
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill="currentColor"
    width="24" 
    height="24"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Gallery data with updated images: balloon arches, white cake columns, teddy bears, hot air balloons
const galleries = {
  matrimoni: {
    title: "Matrimoni",
    description: "Allestimenti eleganti con archi di palloncini organici, fiori freschi, colonne bianche con torte scenografiche e decorazioni raffinate.",
    icon: Heart,
    images: [
      "https://images.unsplash.com/photo-1757589227072-0dd17af42433?w=800",
      "https://images.pexels.com/photos/5610373/pexels-photo-5610373.jpeg?auto=compress&cs=tinysrgb&w=800",
      "https://images.unsplash.com/photo-1678473289821-1818e3f82e9a?w=800",
      "https://images.unsplash.com/photo-1764380746875-7a2dd20c9b80?w=800",
      "https://images.unsplash.com/photo-1757589166064-ef528f309c94?w=800"
    ]
  },
  battesimi: {
    title: "Battesimi e Nascite",
    description: "Decorazioni delicate con mongolfiere di palloncini, orsetti nel cestino di vimini, colonne eleganti e tinte pastello.",
    icon: Baby,
    images: [
      "https://images.unsplash.com/photo-1595543710373-819b8d631147?w=800",
      "https://images.unsplash.com/photo-1760172551485-cea07abc5634?w=800",
      "https://images.pexels.com/photos/12114820/pexels-photo-12114820.jpeg?auto=compress&cs=tinysrgb&w=800",
      "https://images.unsplash.com/photo-1768776183151-a80ccea20f77?w=800",
      "https://images.unsplash.com/photo-1680054902973-1c7d2a04e4b3?w=800"
    ]
  },
  compleanni: {
    title: "Compleanni",
    description: "Allestimenti d'impatto con shimmer wall, numeri giganti, colonne bianche per torte scenografiche e temi personalizzati.",
    icon: Cake,
    images: [
      "https://images.pexels.com/photos/14457430/pexels-photo-14457430.jpeg?auto=compress&cs=tinysrgb&w=800",
      "https://images.unsplash.com/photo-1755704282977-340323fa52df?w=800",
      "https://images.unsplash.com/photo-1741969494307-55394e3e4071?w=800",
      "https://images.unsplash.com/photo-1718878793161-68f0ab82dfae?w=800",
      "https://images.unsplash.com/photo-1627239760089-9b3a54d800aa?w=800"
    ]
  },
  corsi: {
    title: "Corsi di Formazione",
    description: "Impara l'arte della balloon art, creazione di mongolfiere decorative e fiori di carta con i nostri workshop.",
    icon: GraduationCap,
    images: [
      "https://images.unsplash.com/photo-1662583882429-a6d94528d523?w=800",
      "https://images.unsplash.com/photo-1665953495580-c9a5a2a1759d?w=800",
      "https://images.unsplash.com/photo-1662583882409-b774387c9cb3?w=800",
      "https://images.unsplash.com/photo-1665953499482-9d8ef74bd269?w=800"
    ]
  },
  eventi: {
    title: "Eventi e Feste Varie",
    description: "Lauree, inaugurazioni e festività con archi floreali, colonne eleganti e allestimenti su misura.",
    icon: PartyPopper,
    images: [
      "https://images.unsplash.com/photo-1767070806009-152054f6edd5?w=800",
      "https://images.unsplash.com/photo-1666453579043-fef25e9ce099?w=800",
      "https://images.pexels.com/photos/10651682/pexels-photo-10651682.jpeg?auto=compress&cs=tinysrgb&w=800",
      "https://images.unsplash.com/photo-1668574568234-a81693ac9d0c?w=800"
    ]
  }
};

// Carousel Component
const GalleryCarousel = ({ images, title }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true, 
    align: 'start',
    slidesToScroll: 1 
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="relative">
      <div className="embla overflow-hidden rounded-2xl" ref={emblaRef}>
        <div className="embla__container flex gap-4">
          {images.map((img, idx) => (
            <div 
              key={idx} 
              className="embla__slide flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0"
            >
              <div className="gallery-slide rounded-2xl overflow-hidden aspect-[4/3] bg-[#E8E1DA]">
                <img 
                  src={img} 
                  alt={`${title} ${idx + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <button
        onClick={scrollPrev}
        disabled={!canScrollPrev}
        className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-md flex items-center justify-center hover:bg-white transition-all disabled:opacity-50 z-10"
        data-testid={`carousel-prev-${title.toLowerCase().replace(/\s+/g, '-')}`}
        aria-label="Precedente"
      >
        <ChevronLeft className="w-5 h-5 text-[#3D3835]" />
      </button>
      
      <button
        onClick={scrollNext}
        disabled={!canScrollNext}
        className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-md flex items-center justify-center hover:bg-white transition-all disabled:opacity-50 z-10"
        data-testid={`carousel-next-${title.toLowerCase().replace(/\s+/g, '-')}`}
        aria-label="Successivo"
      >
        <ChevronRight className="w-5 h-5 text-[#3D3835]" />
      </button>
    </div>
  );
};

// Header Component
const Header = ({ mobileMenuOpen, setMobileMenuOpen }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#servizi', label: 'Servizi' },
    { href: '#galleria', label: 'Galleria' },
    { href: '#corsi', label: 'Corsi' },
    { href: '#contatti', label: 'Contatti' }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass-header shadow-sm' : 'bg-transparent'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2" data-testid="header-logo">
            <span className={`font-serif text-xl sm:text-2xl font-semibold tracking-tight ${scrolled ? 'text-[#3D3835]' : 'text-white'}`}>
              La Bottega delle Feste
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a 
                key={link.href}
                href={link.href}
                className={`text-sm font-medium transition-colors hover:text-[#B5C1B4] ${
                  scrolled ? 'text-[#3D3835]' : 'text-white'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Social Icons & CTA */}
          <div className="hidden md:flex items-center gap-4">
            <div className="flex items-center gap-2">
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`social-icon ${scrolled ? '' : 'bg-white/20 text-white hover:bg-white hover:text-[#3D3835]'}`}
                data-testid="header-facebook"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`social-icon ${scrolled ? '' : 'bg-white/20 text-white hover:bg-white hover:text-[#3D3835]'}`}
                data-testid="header-instagram"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a 
                href="https://tiktok.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`social-icon ${scrolled ? '' : 'bg-white/20 text-white hover:bg-white hover:text-[#3D3835]'}`}
                data-testid="header-tiktok"
                aria-label="TikTok"
              >
                <TikTokIcon className="w-4 h-4" />
              </a>
            </div>
            <a 
              href="#contatti"
              className="bg-[#B5C1B4] text-[#2C352D] hover:bg-[#A3B0A2] rounded-full px-6 py-2 text-sm font-medium transition-all hover:-translate-y-0.5"
              data-testid="header-cta"
            >
              Richiedi Preventivo
            </a>
          </div>

          {/* Mobile menu button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden p-2 rounded-lg ${scrolled ? 'text-[#3D3835]' : 'text-white'}`}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 bg-white rounded-2xl shadow-lg mt-2 animate-fade-in-up" data-testid="mobile-menu">
            <nav className="flex flex-col gap-2 px-4">
              {navLinks.map((link) => (
                <a 
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-2 text-[#3D3835] hover:text-[#B5C1B4] transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <div className="flex items-center gap-3 pt-4 border-t border-[#E8E1DA] mt-2">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-icon">
                  <Facebook className="w-4 h-4" />
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-icon">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="social-icon">
                  <TikTokIcon className="w-4 h-4" />
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section - Coming Soon / Work in Progress
const HeroSection = () => (
  <section className="hero-section relative min-h-[85vh] flex items-center justify-center" data-testid="hero-section">
    <div 
      className="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style={{ 
        backgroundImage: `url('https://images.unsplash.com/photo-1755403055037-90bc80b072bb?w=1920')` 
      }}
    />
    <div className="hero-overlay" style={{ background: 'linear-gradient(to bottom, rgba(250, 248, 245, 0.7), rgba(250, 248, 245, 0.9))' }} />
    
    <div className="hero-content relative z-10 text-center px-4 max-w-4xl mx-auto">
      <div className="inline-block bg-[#F2D8D5] text-[#4A3330] text-xs sm:text-sm uppercase tracking-[0.2em] px-6 py-2 rounded-full mb-6 animate-fade-in-up font-sans">
        🚧 Sito in costruzione
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-semibold text-[#3D3835] mb-6 animate-fade-in-up animation-delay-100 leading-tight">
        Stiamo preparando qualcosa di speciale
      </h1>
      <p className="text-base sm:text-lg text-[#7A736E] max-w-2xl mx-auto mb-4 animate-fade-in-up animation-delay-200 font-sans">
        <strong className="text-[#3D3835]">La Bottega delle Feste</strong> sta arrivando a Trissino!
      </p>
      <p className="text-base sm:text-lg text-[#7A736E] max-w-2xl mx-auto mb-8 animate-fade-in-up animation-delay-200 font-sans">
        Balloon Art e decorazioni artigianali per matrimoni, battesimi, compleanni e ogni evento speciale nella provincia di Vicenza
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animation-delay-300">
        <a 
          href="#contatti"
          className="bg-[#B5C1B4] text-[#2C352D] hover:bg-[#A3B0A2] rounded-full px-8 py-3 font-medium transition-all hover:-translate-y-1 shadow-lg"
          data-testid="hero-cta-primary"
        >
          Contattaci per Info
        </a>
        <a 
          href="#galleria"
          className="bg-[#3D3835] text-white hover:bg-[#2C352D] rounded-full px-8 py-3 font-medium transition-all border border-[#3D3835]"
          data-testid="hero-cta-secondary"
        >
          Scopri i Nostri Lavori
        </a>
      </div>
      
      {/* Social links */}
      <div className="flex items-center justify-center gap-4 mt-8 animate-fade-in-up animation-delay-400">
        <span className="text-sm text-[#7A736E] font-sans">Seguici:</span>
        <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-icon">
          <Facebook className="w-4 h-4" />
        </a>
        <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-icon">
          <Instagram className="w-4 h-4" />
        </a>
        <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="social-icon">
          <TikTokIcon className="w-4 h-4" />
        </a>
      </div>
    </div>

    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
      <ChevronLeft className="w-8 h-8 text-[#3D3835] rotate-[-90deg]" />
    </div>
  </section>
);

// About Section
const AboutSection = () => (
  <section className="py-20 sm:py-32 bg-[#FAF8F5]" id="servizi" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="relative">
          <div className="aspect-[4/5] rounded-2xl overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1522173096622-763cc9d35a3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxydXN0aWMlMjB3b29kJTIwc29mdCUyMHRleHR1cmUlMjBlbXB0eSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc1ODUzMTg0fDA&ixlib=rb-4.1.0&q=85"
              alt="Texture naturale"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#F2D8D5] rounded-2xl -z-10" />
        </div>
        
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-4 font-sans">Chi Siamo</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#3D3835] mb-6 leading-tight">
            Artigiani della Festa dal cuore di Trissino
          </h2>
          <div className="space-y-4 text-[#7A736E] leading-relaxed font-sans">
            <p>
              Benvenuti a <strong className="text-[#3D3835]">La Bottega delle Feste</strong>, il vostro punto di riferimento 
              per allestimenti e decorazioni nel cuore della provincia di Vicenza.
            </p>
            <p>
              Ogni creazione nasce dalla passione per l'artigianalità e l'amore per i dettagli. 
              Utilizziamo materiali naturali come <strong className="text-[#3D3835]">pampas, fiori freschi e fiori di carta fatti a mano</strong> 
              per creare atmosfere uniche e indimenticabili.
            </p>
            <p>
              Dai matrimoni romantici ai battesimi delicati, dai compleanni colorati alle feste aziendali eleganti, 
              trasformiamo ogni spazio in un sogno che prende vita.
            </p>
          </div>
          
          <div className="flex items-center gap-6 mt-8">
            <div className="flex items-center gap-2 text-[#3D3835]">
              <MapPin className="w-5 h-5 text-[#B5C1B4]" />
              <span className="text-sm font-sans">Trissino (VI)</span>
            </div>
            <div className="flex items-center gap-2 text-[#3D3835]">
              <Heart className="w-5 h-5 text-[#F2D8D5]" />
              <span className="text-sm font-sans">100% Artigianale</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Gallery Section
const GallerySection = ({ category, data, isAlternate }) => {
  const Icon = data.icon;
  
  return (
    <section 
      className={`py-16 sm:py-24 ${isAlternate ? 'bg-[#FAF8F5]' : 'bg-white'}`}
      id={category === 'corsi' ? 'corsi' : undefined}
      data-testid={`gallery-${category}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-full bg-[#B5C1B4]/20 flex items-center justify-center">
            <Icon className="w-6 h-6 text-[#B5C1B4]" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] font-sans">Galleria</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#3D3835]">{data.title}</h2>
          </div>
        </div>
        <p className="text-[#7A736E] mb-8 max-w-2xl font-sans">{data.description}</p>
        
        <GalleryCarousel images={data.images} title={data.title} />
      </div>
    </section>
  );
};

// Contact & Calendar Section
const ContactSection = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [availability, setAvailability] = useState({ blocked_dates: [], booked_dates: [] });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefono: '',
    tipologia: '',
    messaggio: ''
  });

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API}/availability`);
      const data = response?.data;
      setAvailability({
        blocked_dates: Array.isArray(data?.blocked_dates) ? data.blocked_dates : [],
        booked_dates: Array.isArray(data?.booked_dates) ? data.booked_dates : []
      });
    } catch (error) {
      console.error('Error fetching availability:', error);
      // Mantieni valori di default se il backend non risponde
      setAvailability({ blocked_dates: [], booked_dates: [] });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, tipologia: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.email || !formData.tipologia || !selectedDate) {
      toast.error('Per favore compila tutti i campi obbligatori');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const payload = {
        ...formData,
        data_evento: format(selectedDate, 'yyyy-MM-dd')
      };
      
      await axios.post(`${API}/booking`, payload);
      toast.success('Richiesta inviata con successo! Ti contatteremo presto.');
      
      setFormData({
        nome: '',
        email: '',
        telefono: '',
        tipologia: '',
        messaggio: ''
      });
      setSelectedDate(null);
      fetchAvailability();
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error('Si è verificato un errore. Riprova più tardi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDateDisabled = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return availability?.blocked_dates?.includes(dateStr) || false;
  };

  const isDateBooked = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return availability?.booked_dates?.includes(dateStr) || false;
  };

  return (
    <section className="py-20 sm:py-32 bg-[#B5C1B4]/10" id="contatti" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-4 font-sans">Prenota</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#3D3835] mb-4">
            Richiedi un Appuntamento
          </h2>
          <p className="text-[#7A736E] max-w-2xl mx-auto font-sans">
            Seleziona una data disponibile e compila il modulo per richiedere un preventivo gratuito
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Calendar */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-[#E8E1DA]">
            <div className="flex items-center gap-3 mb-6">
              <CalendarIcon className="w-5 h-5 text-[#B5C1B4]" />
              <h3 className="font-serif text-xl text-[#3D3835]">Calendario Disponibilità</h3>
            </div>
            
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => date < new Date() || isDateDisabled(date)}
                locale={it}
                className="rounded-xl"
                modifiers={{
                  booked: (date) => isDateBooked(date)
                }}
                modifiersStyles={{
                  booked: { 
                    backgroundColor: '#B1C5D4', 
                    color: '#1F303D',
                    borderRadius: '8px'
                  }
                }}
                data-testid="availability-calendar"
              />
            </div>
            
            <div className="flex items-center justify-center gap-6 mt-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#B5C1B4]" />
                <span className="text-[#7A736E] font-sans">Selezionato</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#B1C5D4]" />
                <span className="text-[#7A736E] font-sans">Prenotato</span>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-[#E8E1DA]">
            <h3 className="font-serif text-xl text-[#3D3835] mb-6">Modulo di Contatto</h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#3D3835] mb-1.5 font-sans">
                  Nome e Cognome *
                </label>
                <Input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  placeholder="Il tuo nome"
                  className="form-input"
                  data-testid="form-nome"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3D3835] mb-1.5 font-sans">
                  Email *
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="La tua email"
                  className="form-input"
                  data-testid="form-email"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3D3835] mb-1.5 font-sans">
                  Telefono / WhatsApp
                </label>
                <Input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  placeholder="Il tuo numero"
                  className="form-input"
                  data-testid="form-telefono"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3D3835] mb-1.5 font-sans">
                  Tipologia Servizio *
                </label>
                <Select value={formData.tipologia} onValueChange={handleSelectChange}>
                  <SelectTrigger className="form-input" data-testid="form-tipologia">
                    <SelectValue placeholder="Seleziona un servizio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="allestimento_evento" data-testid="option-allestimento">
                      Allestimento Evento
                    </SelectItem>
                    <SelectItem value="corso_formazione" data-testid="option-corso">
                      Corso di Formazione
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3D3835] mb-1.5 font-sans">
                  Data Evento: {selectedDate ? format(selectedDate, 'dd MMMM yyyy', { locale: it }) : 'Seleziona dal calendario'}
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3D3835] mb-1.5 font-sans">
                  Messaggio
                </label>
                <Textarea
                  name="messaggio"
                  value={formData.messaggio}
                  onChange={handleInputChange}
                  placeholder="Descrivi il tuo evento e le tue esigenze..."
                  className="form-input min-h-[100px] resize-none"
                  data-testid="form-messaggio"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#B5C1B4] text-[#2C352D] hover:bg-[#A3B0A2] rounded-full py-3 font-medium transition-all"
                data-testid="form-submit"
              >
                {isSubmitting ? 'Invio in corso...' : 'Invia Richiesta'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="bg-[#2C352D] text-[#FAF8F5] py-16 sm:py-20" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
        {/* Brand */}
        <div className="lg:col-span-2">
          <h3 className="font-serif text-2xl sm:text-3xl mb-4">La Bottega delle Feste</h3>
          <p className="text-[#FAF8F5]/70 mb-6 max-w-md font-sans leading-relaxed">
            Balloon Art e decorazioni artigianali per trasformare ogni evento in un momento indimenticabile. 
            Serviamo Trissino e tutta la provincia di Vicenza.
          </p>
          <div className="flex items-center gap-3">
            <a 
              href="https://facebook.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-[#FAF8F5]/10 flex items-center justify-center hover:bg-[#B5C1B4] hover:text-[#2C352D] transition-all"
              data-testid="footer-facebook"
              aria-label="Facebook"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <a 
              href="https://instagram.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-[#FAF8F5]/10 flex items-center justify-center hover:bg-[#B5C1B4] hover:text-[#2C352D] transition-all"
              data-testid="footer-instagram"
              aria-label="Instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a 
              href="https://tiktok.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-[#FAF8F5]/10 flex items-center justify-center hover:bg-[#B5C1B4] hover:text-[#2C352D] transition-all"
              data-testid="footer-tiktok"
              aria-label="TikTok"
            >
              <TikTokIcon className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-semibold text-lg mb-4 font-sans">Link Rapidi</h4>
          <nav className="flex flex-col gap-2">
            <a href="#servizi" className="text-[#FAF8F5]/70 hover:text-[#B5C1B4] transition-colors font-sans">Servizi</a>
            <a href="#galleria" className="text-[#FAF8F5]/70 hover:text-[#B5C1B4] transition-colors font-sans">Galleria</a>
            <a href="#corsi" className="text-[#FAF8F5]/70 hover:text-[#B5C1B4] transition-colors font-sans">Corsi</a>
            <a href="#contatti" className="text-[#FAF8F5]/70 hover:text-[#B5C1B4] transition-colors font-sans">Contatti</a>
          </nav>
        </div>

        {/* Contact Info */}
        <div>
          <h4 className="font-semibold text-lg mb-4 font-sans">Contatti</h4>
          <div className="flex flex-col gap-3">
            <a href="mailto:danijela.vidakovic03@gmail.com" className="flex items-center gap-2 text-[#FAF8F5]/70 hover:text-[#B5C1B4] transition-colors">
              <Mail className="w-4 h-4" />
              <span className="font-sans text-sm">danijela.vidakovic03@gmail.com</span>
            </a>
            <a href="tel:+393661134236" className="flex items-center gap-2 text-[#FAF8F5]/70 hover:text-[#B5C1B4] transition-colors">
              <Phone className="w-4 h-4" />
              <span className="font-sans text-sm">+39 366 113 4236</span>
            </a>
            <div className="flex items-start gap-2 text-[#FAF8F5]/70">
              <MapPin className="w-4 h-4 mt-0.5" />
              <span className="font-sans text-sm">Trissino (VI), Italia</span>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-[#FAF8F5]/10 mt-12 pt-8 text-center">
        <p className="text-[#FAF8F5]/50 text-sm font-sans">
          © {new Date().getFullYear()} La Bottega delle Feste. Tutti i diritti riservati.
        </p>
      </div>
    </div>
  </footer>
);

// Main App Component
function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Show mystery landing page if enabled
  if (SHOW_MYSTERY_LANDING) {
    return <LandingMystery />;
  }

  // Otherwise show the main site
  return (
    <div className="App min-h-screen bg-[#FAF8F5]">
      <div className="noise-overlay" />
      <Toaster position="top-right" richColors />
      
      <Header mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      
      <main>
        <HeroSection />
        <AboutSection />
        
        <div id="galleria">
          {Object.entries(galleries).map(([key, data], index) => (
            <GallerySection 
              key={key}
              category={key}
              data={data}
              isAlternate={index % 2 === 1}
            />
          ))}
        </div>
        
        <ContactSection />
      </main>
      
      <Footer />
    </div>
  );
}

export default App;
